import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import {
  getOrigin,
  canUsePhysicalQrCodeReader,
  getSelfServiceAvailableTypes,
  isKioskFull as isKioskFullSelector,
} from 'modules/dealers/selectors';
import { isOffline as isOfflineSelector } from 'modules/healthCheck/selectors';

import { Button, Icon } from 'components/ui';
import { ButtonType } from 'components/ui/Button';

import { getSelfServiceType } from 'modules/selfServices/selectors';
import {  SelfServiceType } from 'modules/selfServices/types/SelfService';

import { useSelector } from '../../hooks';

import type { HomeButtonProps } from './index';
import HomeButton from './HomeButton';
import { BASE_ANALYTICS_CONFIG } from './constants';


const SelfServiceTypeButtons: React.FC<HomeButtonProps> = ({ onClick }) => {
  const origin = useSelector(getOrigin);
  const type = useSelector(getSelfServiceType);
  const disabled = useSelector(isOfflineSelector);
  const isKioskFull = useSelector(isKioskFullSelector);
  const displayQrCodeHints = useSelector(canUsePhysicalQrCodeReader);

  const selfServiceAvailableTypes = useSelector(getSelfServiceAvailableTypes);

  const displayCheckInButton = type === SelfServiceType.CHECK_IN ||
    (!type && selfServiceAvailableTypes[SelfServiceType.CHECK_IN]?.includes(origin));

  const displayCheckOutButton = type === SelfServiceType.CHECK_OUT ||
   (!type && selfServiceAvailableTypes[SelfServiceType.CHECK_OUT]?.includes(origin));

  const shouldShowBreakdown = selfServiceAvailableTypes[SelfServiceType.BREAKDOWN]?.includes(origin);

  const handleClick = (selectedType: SelfServiceType) => () => onClick({ type: selectedType });

  return (
    <div className="pb-12 pt-6 w-full max-w-[85%] mx-auto sm:mt-auto sm:px-(--size-padding-x-home-buttons,0px) sm:pb-(--size-padding-bottom-home-buttons,3rem) sm:max-w-(--max-w-home-buttons,85%)">
      <div
        className={classNames('flex gap-8 pt-6 kiosk:w-full sm:flex-col sm:gap-home-buttons-gap', { 'mb-12 sm:mb-(--size-margin-bottom-home-type-buttons,3rem)': !displayQrCodeHints })}
      >
        {displayCheckInButton && (
          <HomeButton
            disabled={disabled}
            testId="check-in-button"
            icon="fb-icon--hand-check-in"
            onClick={handleClick(SelfServiceType.CHECK_IN)}
            analytics={{ ...BASE_ANALYTICS_CONFIG, action: 'CHECK_IN login' }}
            label={<FormattedMessage id="home.checkIn" defaultMessage="Check-in" />}
          />
        )}
        {displayCheckOutButton && (
          <HomeButton
            disabled={disabled}
            testId="check-out-button"
            icon="fb-icon--hand-check-out"
            onClick={handleClick(SelfServiceType.CHECK_OUT)}
            analytics={{ ...BASE_ANALYTICS_CONFIG, action: 'CHECK_OUT login' }}
            label={<FormattedMessage id="home.checkOut" defaultMessage="Check-out" />}
          />
        )}
      </div>
      {displayQrCodeHints &&
      <div data-testid="qr-code-hint" className="text-center text-white mt-8 mb-8 text-xs">
        <FormattedMessage
          id="login.qrCodeVideo"
          defaultMessage="If you have a QR code, you can scan it using the kiosk’s QR code reader to quickly access your appointment."
        />
      </div>
      }
      {shouldShowBreakdown && (
        <>
          <Button
            analytics={{
              ...BASE_ANALYTICS_CONFIG,
              action: 'BREAKDOWN login',
            }}
            testId="breakdown-button"
            type={ButtonType.PRIMARY}
            disabled={disabled}
            disabledStyle={isKioskFull}
            className="flex items-center justify-center w-full drop-shadow-md! btn-home-primary rounded-soft-button! sm:p-3!"
            onClick={handleClick(SelfServiceType.BREAKDOWN)}
          >
            <Icon className="fb-icon--no-appointments mr-8 text-btn-primary-icon-home! text-btn-home-primary-text text-5xl! sm:mr-4! sm:text-icon-xl!" />
            <span className="sm:whitespace-nowrap! text-l!"> <FormattedMessage id="home.breakdown" defaultMessage="No appointment" /></span>
          </Button>
        </>
      )}
    </div>
  );
};

export default SelfServiceTypeButtons;
